import { Box, Divider, GlobalStyles, Stack, Typography, type SelectProps } from '@mui/material';
import { COMMITMENT_TYPES } from '@vy/types';
import type { Currency, UsageByDateBreakdown } from '@vy/types';
import { extract, formatNumber, isEmptyArray, isNotNullOrUndefined, sumBy } from '@vy/utils';
import React, { useMemo, type ReactNode } from 'react';
import type { SelectItem } from '../../../../../../../../components/new-design/molecules';
import { VyCheckboxSelect } from '../../../../../../../../components/new-design/molecules';
import { getCommitmentLabel } from '../../../../../../../../services/commitment/getCommitmentLabel';
import { ProductUsageBreakdownChart } from '../../ViewProducts/ProductUsageBreakdownChart/ProductUsageBreakdownChart';
import type { ProductUsageBreakdownDateResolution } from '../../ViewProducts/ProductUsageBreakdownChart/product-usage-breakdown-chart-types/product-usage-breakdown-date-resolution';
import { PRODUCT_USAGE_BREAKDOWN_DATE_RESOLUTIONS } from '../../ViewProducts/ProductUsageBreakdownChart/product-usage-breakdown-chart-types/product-usage-breakdown-date-resolution';
import { VyDivider, VySelect } from '../../../../../../../../components/new-design/atoms';
import { VyStack } from '@vy/ui-design-system';

type DateResolutionOption = {
  key: ProductUsageBreakdownDateResolution;
  label: string;
};

const DATE_RESOLUTION_OPTIONS: DateResolutionOption[] = [
  { key: PRODUCT_USAGE_BREAKDOWN_DATE_RESOLUTIONS.DAY, label: 'Daily' },
  { key: PRODUCT_USAGE_BREAKDOWN_DATE_RESOLUTIONS.MONTH, label: 'Monthly' },
];

export type CommitmentTotals = {
  units?: { amount: number; formatted: string };
  price?: { amount: number; formatted: string };
};

type ContractUsageDialogContentProps = {
  isLoading: boolean;
  usageByDateBreakdowns: UsageByDateBreakdown[];
  dateResolution: ProductUsageBreakdownDateResolution;
  commitmentTotals?: CommitmentTotals;
  currency?: Currency;
  usageProducts?: SelectItem[];
  selectedProductIds?: string[];
  creditsSlot?: ReactNode;
  onDateResolutionChange?: (resolution: ProductUsageBreakdownDateResolution) => void;
  onSelectedProductsChange?: (productIds: string[]) => void;
};

export const ContractUsageDialogContent: React.FC<ContractUsageDialogContentProps> = ({
  isLoading,
  usageByDateBreakdowns,
  dateResolution,
  commitmentTotals,
  usageProducts,
  selectedProductIds,
  creditsSlot,
  onDateResolutionChange,
  onSelectedProductsChange,
}) => {
  const filteredUsageByDateBreakdowns = useMemo(() => {
    if (!selectedProductIds || isEmptyArray(selectedProductIds)) {
      return usageByDateBreakdowns;
    }

    return usageByDateBreakdowns.map((breakdown) => {
      const customerUsage = breakdown.customerUsage.filter(({ customerId }) =>
        selectedProductIds.includes(customerId),
      );
      const units = sumBy(customerUsage, (entry) => entry.units);

      return { ...breakdown, customerUsage, units };
    });
  }, [usageByDateBreakdowns, selectedProductIds]);

  const totalUnits = useMemo(
    () => sumBy(filteredUsageByDateBreakdowns, (breakdown) => breakdown.units),
    [filteredUsageByDateBreakdowns],
  );

  const handleSelectionChange = (selectedItems: SelectItem[]) => {
    onSelectedProductsChange?.(extract(selectedItems, 'value'));
  };

  const hasMultipleUsageProducts = (usageProducts?.length ?? 0) > 1;
  const showProductFilter = hasMultipleUsageProducts && isNotNullOrUndefined(onSelectedProductsChange);
  const showResolutionSwitcher = isNotNullOrUndefined(onDateResolutionChange);
  const hasNoUsageData = !isLoading && isEmptyArray(usageByDateBreakdowns);
  const showHeaderRow = showProductFilter || isNotNullOrUndefined(creditsSlot);

  return (
    <>
      <GlobalStyles
        styles={{
          '.MuiChartsTooltip-root, [role="tooltip"]': { zIndex: '10001 !important' },
        }}
      />
      {showHeaderRow && (
        <VyStack sx={{ marginBottom: 2 }}>
          <Divider sx={{ mx: -3, mb: 2 }} />
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            {isNotNullOrUndefined(creditsSlot) ? (
              <Typography variant="body1" sx={{ fontWeight: '500' }}>
                Available credits
              </Typography>
            ) : (
              <span />
            )}
            {showProductFilter && (
              <VyCheckboxSelect
                data-vy="contract-usage-dialog-product-select"
                onSelectionChange={handleSelectionChange}
                withChips
                withSelectAll
                placeholder="Filter Products"
                selectItems={usageProducts ?? []}
                maxChipsInInputArea={1}
                MenuProps={{ sx: { maxHeight: '300px', zIndex: 10001 } }}
              />
            )}
          </Stack>
        </VyStack>
      )}

      {isNotNullOrUndefined(creditsSlot) && <Box sx={{ marginBottom: 2 }}>{creditsSlot}</Box>}

      {hasNoUsageData ? (
        <Stack sx={{ alignItems: 'center', justifyContent: 'center', height: 400 }}>
          <Typography variant="body2" color="text.secondary">
            No usage data available
          </Typography>
        </Stack>
      ) : (
        <Box
          sx={{
            background: '#F5F6FA',
            padding: '16px 20px',
            borderRadius: '10px',
            border: '1px solid #E8EBF2',
          }}
        >
          <Stack
            direction="row"
            sx={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}
          >
            <Stack
              direction="row"
              spacing={1}
              divider={<VyDivider orientation="vertical" flexItem />}
              sx={{ alignItems: 'center', flexWrap: 'wrap' }}
            >
              <Typography variant="body1">{`${usageProducts?.length ?? 0} products`}</Typography>
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Total units:
                </Typography>
                <Typography variant="body2" color="text.primary" sx={{ paddingRight: 1 }}>
                  {formatNumber(totalUnits)}
                </Typography>
              </Stack>
              {isNotNullOrUndefined(commitmentTotals?.units) && (
                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {getCommitmentLabel(COMMITMENT_TYPES.Units)}:
                  </Typography>
                  <Typography variant="body2" color="text.primary" sx={{ paddingRight: 1 }}>
                    {commitmentTotals.units.formatted}
                  </Typography>
                </Stack>
              )}
              {isNotNullOrUndefined(commitmentTotals?.price) && (
                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {getCommitmentLabel(COMMITMENT_TYPES.Price)}:
                  </Typography>
                  <Typography variant="body2" color="text.primary" sx={{ paddingRight: 1 }}>
                    {commitmentTotals.price.formatted}
                  </Typography>
                </Stack>
              )}
            </Stack>

            {showResolutionSwitcher && (
              <VySelect
                options={DATE_RESOLUTION_OPTIONS}
                selectedOption={DATE_RESOLUTION_OPTIONS.find((option) => option.key === dateResolution)}
                onSelectionChange={(item) => item && onDateResolutionChange?.(item.key)}
                render={(item) => item.label}
                keyExtractor={(item) => item.key}
                selectProps={
                  {
                    sx: {
                      height: '32px',
                      borderRadius: '8px',
                      backgroundColor: '#fff',
                    },
                    MenuProps: { sx: { zIndex: 10001 } },
                    'data-vy': 'contract-usage-dialog-resolution-select',
                  } as SelectProps
                }
              />
            )}
          </Stack>
          <ProductUsageBreakdownChart
            isLoading={isLoading}
            dateResolution={dateResolution}
            usageByDateBreakdowns={filteredUsageByDateBreakdowns}
          />
        </Box>
      )}
    </>
  );
};
